import { memo, useCallback, useMemo } from 'react';

import { useHistory } from 'react-router-dom';

import { AccessCentreRole } from 'domains/accessCentre/types';
import MoreOptionsDropdown from 'domains/core/components/MoreOptionsDropdown';
import { URLPaths } from 'models/enums';

export type RolesTableOptionsCellProps = {
    role: AccessCentreRole;
    showActions?: boolean;
    /** Opens duplicate dialog (or equivalent) instead of navigating to copy flow. */
    onDuplicateRole?: (role: AccessCentreRole) => void;
};

/**
 * Memoized cell that renders the more options dropdown for a role row (View, Edit, Duplicate).
 */
const RolesTableOptionsCell = ({ role, showActions = true, onDuplicateRole }: RolesTableOptionsCellProps) => {
    const history = useHistory();

    const handleView = useCallback(() => {
        history.push(`${URLPaths.SETTINGS_ROLES}?id=${role.id}`);
    }, [history, role.id]);

    const handleEdit = useCallback(() => {
        history.push(`${URLPaths.SETTINGS_ROLES}?id=${role.id}&mode=edit`);
    }, [history, role.id]);

    const handleDuplicate = useCallback(() => {
        if (onDuplicateRole) {
            onDuplicateRole(role);
            return;
        }
        history.push(`${URLPaths.SETTINGS_ROLES}?id=${role.id}&isCopy=true`);
    }, [history, role, onDuplicateRole]);

    const isSystemRoleWithoutEdit = role.system_role_type === 'admin' || role.system_role_type === 'no_access';

    const isNoAccessRole = role.system_role_type === 'no_access';

    const menuItems = useMemo(() => {
        const items: { name: string; onClick: () => void; testId: string }[] = [
            { name: 'View', onClick: handleView, testId: `access-center-role-view-${role.id}` },
        ];
        if (!isSystemRoleWithoutEdit) {
            items.push({ name: 'Edit', onClick: handleEdit, testId: `access-center-role-edit-${role.id}` });
        }
        if (!isNoAccessRole) {
            items.push({
                name: 'Duplicate',
                onClick: handleDuplicate,
                testId: `access-center-role-duplicate-${role.id}`,
            });
        }
        return items;
    }, [handleView, handleEdit, handleDuplicate, role.id, isSystemRoleWithoutEdit, isNoAccessRole]);

    if (!showActions) {
        return null;
    }

    return <MoreOptionsDropdown buttonTestId={`access-center-role-options-${role.id}`} menuItems={menuItems} />;
};

export default memo(RolesTableOptionsCell);
